import { PlainDate } from "@sa-nu/shared-utils/date";

import type { FragmentType } from "../../../../__generated__/gql";
import { graphql } from "../../../../__generated__/gql";
import { useFragment } from "../../../../__generated__/gql";
import {
  AccommodationFacilityType,
  type AccommodationReservationProvider,
} from "../../../../__generated__/gql/graphql";
import { getAccommodationName } from "../../../../utils";
import type { AccommodationAndPeriodMessages } from "../accommodation-and-period/accommodation-and-period";
import { AccommodationAndPeriod } from "../accommodation-and-period/accommodation-and-period";

import { isNowEarlierThanTwoDaysBeforeCheckIn } from "./accommodation-reservation-info";

const Fragment = graphql(`
  fragment DraftAccommodationReservationInfo on DraftAccommodationReservation {
    firstDate
    lastDate
    reservedFrom
    accommodation {
      name
      code
      site {
        name
        slug
        area {
          slug
        }
      }
      architectureType {
        name
        series
      }
      facilityTypes
      roomType {
        displayName
      }
    }
    options {
      bringDog
      useEvCharger
    }
    stayTicketUnit {
      reservationInfo {
        name
      }
    }
  }
`);

export function DraftAccommodationReservationInfo({
  provider,
  draftReservation,
  className,
  messages,
  isRoomTypeReservation,
}: {
  provider: AccommodationReservationProvider;
  draftReservation: FragmentType<typeof Fragment>;
  className?: string;
  messages?: AccommodationAndPeriodMessages;
  /** 部屋タイプ予約として扱うか。true のとき RoomChangeNotice を表示し「オプション」行を部屋タイプ名にする */
  isRoomTypeReservation?: boolean;
}) {
  const draftData = useFragment(Fragment, draftReservation);

  const accommodationName = getAccommodationName({
    provider,
    reservedFrom: draftData.reservedFrom,
    accommodation: draftData.accommodation,
  });
  const accommodationCode =
    provider.__typename ===
      "OnlineTravelAgentAccommodationReservationProvider" &&
    isNowEarlierThanTwoDaysBeforeCheckIn(draftData.reservedFrom)
      ? undefined
      : draftData.accommodation.code;
  const architectureName = draftData.accommodation.architectureType.name;
  const checkInDate = PlainDate.from(draftData.firstDate);
  const checkOutDate = PlainDate.from(draftData.lastDate);
  const ticketMasterName = draftData.stayTicketUnit?.reservationInfo?.name;
  const roomTypeName = draftData.accommodation.roomType?.displayName;
  const facilityTypes = draftData.accommodation.facilityTypes.filter((type) => {
    if (type === AccommodationFacilityType.DogFriendly) {
      return draftData.options.bringDog;
    } else if (type === AccommodationFacilityType.EvCharger) {
      return draftData.options.useEvCharger;
    } else {
      return true;
    }
  });

  return (
    <AccommodationAndPeriod
      accommodationName={accommodationName}
      accommodationCode={accommodationCode}
      architectureName={architectureName}
      checkInDate={checkInDate}
      checkOutDate={checkOutDate}
      className={className}
      facilityTypes={facilityTypes}
      ticketMasterName={ticketMasterName}
      messages={messages}
      isRoomTypeReservation={isRoomTypeReservation}
      roomTypeName={roomTypeName}
      siteName={draftData.accommodation.site.name}
    />
  );
}
